import type { RemovableRef } from '@vueuse/core'
import { debounce } from 'lodash'
import * as Sentry from '@sentry/browser'
import type { Device } from '@twilio/voice-sdk'

export function useAudioSources (callDevice: Ref<Device | null>) {
  const snackbar = useSnackbar()

  const storedInputSource: RemovableRef<string> = useLocalStorage(
    'inputSource',
    'default'
  )
  const storedOutputSource: RemovableRef<string> = useLocalStorage(
    'outputSource',
    'default'
  )
  const availableInputSources = ref<MediaDeviceInfo[]>([])
  const inputSource = ref<string | null>(null)
  const availableOutputSources = ref<MediaDeviceInfo[]>([])
  const outputSource = ref<string | null>(null)

  const removeAudioSources = () => {
    callDevice.value?.audio?.removeAllListeners()
    availableInputSources.value = []
    inputSource.value = null
    availableOutputSources.value = []
    outputSource.value = null
  }

  const setAudioSources = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true })
      if (!callDevice.value?.audio) {
        throw new Error('Device audio not found')
      }
      callDevice.value?.audio.removeAllListeners()
      callDevice.value?.audio.on('deviceChange', () =>
        setAudioSourcesDebounced()
      )
      availableInputSources.value = Array.from(
        callDevice.value?.audio.availableInputDevices.values()
      )
      const isStoredInputSourceAvailable = availableInputSources.value.some(
        (source) => {
          return source.deviceId === storedInputSource.value
        }
      )
      inputSource.value = isStoredInputSourceAvailable
        ? storedInputSource.value
        : 'default'
      availableOutputSources.value = Array.from(
        callDevice.value?.audio.availableOutputDevices.values()
      )
      const isStoredOutputSourceAvailable = availableOutputSources.value.some(
        (source) => {
          return source.deviceId === storedOutputSource.value
        }
      )
      outputSource.value = isStoredOutputSourceAvailable
        ? storedOutputSource.value
        : 'default'
      await Promise.all([
        callDevice.value?.audio.setInputDevice(inputSource.value),
        callDevice.value?.audio.speakerDevices.set(outputSource.value),
        callDevice.value?.audio.ringtoneDevices.set(outputSource.value)
      ])
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Failed to get audio sources')
      removeAudioSources()
    }
  }

  const setAudioSourcesDebounced = debounce(setAudioSources, 1000)

  const changeInputSource = async (deviceId: string) => {
    try {
      await callDevice.value?.audio?.setInputDevice(deviceId)
      storedInputSource.value = deviceId
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Failed to set input source')
    }
  }

  const changeOutputSource = async (deviceId: string) => {
    try {
      await Promise.all([
        callDevice.value?.audio?.speakerDevices.set(deviceId),
        callDevice.value?.audio?.ringtoneDevices.set(deviceId)
      ])
      storedOutputSource.value = deviceId
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Failed to set output source')
    }
  }

  return {
    storedInputSource,
    storedOutputSource,
    availableInputSources,
    inputSource,
    availableOutputSources,
    outputSource,
    removeAudioSources,
    setAudioSources,
    setAudioSourcesDebounced,
    changeInputSource,
    changeOutputSource
  }
}
